import React, { useState, useEffect, useRef } from "react"
import { Head } from "@inertiajs/react"
import { Card, CardContent } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Key,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  X,
  Shield,
  Eye,
  Search,
  Database,
} from "lucide-react"

import { UserFormModal } from "@/components/users/user-form-modal"
import { UserPermissionsModal } from "@/components/users/user-permissions-modal"
import { ExternalUserModal } from "@/components/users/external-user-modal"
import { PasswordResetConfirmModal } from "@/components/modals/password-reset-confirm-modal"
import { useToast } from "@/hooks/use-toast"
import { FilterDropdown } from "@/components/filter-dropdown"
import { useMobile } from "@/hooks/use-mobile"
import { useFilterStorage } from "@/hooks/use-filter-storage"
import { useUsers, UserProvider } from "@/lib/contexts/user-context"
import { router, usePage } from "@inertiajs/react"
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout"
import axios from "axios"

export default function UsersPage() {
  const { props } = usePage()
  
  return (
    <>
      <Head title="DME | Users" />
      <AuthenticatedLayout>
              <UserProvider 
          initialUsers={props.users || []} 
          initialCurrentUser={props.user || null}
        >
          <UsersContent
            permissions={props.permissions || {}}
            departments={props.departments || []}
            integrations={props.integrations || []}
          />
        </UserProvider>
      </AuthenticatedLayout>
    </>
  )
}

const defaultUserFilters = {
  searchQuery: "",
  roleFilter: "All",
  statusFilter: "All",
  departmentFilter: "All",
  sortColumn: "id",
  sortDirection: "asc",
  rowsPerPage: 10,
  page: 0,
};

function UsersContent({ permissions, departments, integrations }) {
  const isMobile = useMobile()

  const { filters, updateFilter, updateFilters, resetFilters: resetStoredFilters } = useFilterStorage('users-filters', defaultUserFilters);

  const [isUserFormOpen, setIsUserFormOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false)
  const [userForPasswordReset, setUserForPasswordReset] = useState(null)
  const [isPasswordResetSubmitting, setIsPasswordResetSubmitting] = useState(false)
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false)
  const [userForPermissions, setUserForPermissions] = useState(null)
  const [isExternalUserModalOpen, setIsExternalUserModalOpen] = useState(false)
  const [userForExternalIds, setUserForExternalIds] = useState(null)
  const { toast } = useToast()
  const { users, addUser, updateUser, deleteUser, currentUser } = useUsers()
  const { props } = usePage()

  // Handle URL user parameter from global search (open modal for specific user)
  // and ?action=new URL parameter to open user form from quick actions menu
  const urlUserApplied = useRef(false);
  useEffect(() => {
    if (urlUserApplied.current) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('user');
    
    if (userId) {
      urlUserApplied.current = true;
      const user = users.find(u => u.id === parseInt(userId));
      if (user) {
        setSelectedUser(user);
        setIsUserFormOpen(true);
      }
    }
    
    // Handle ?action=new URL parameter to open user form from quick actions menu
    if (urlParams.get('action') === 'new' && permissions?.can_create_users) {
      setSelectedUser(null);
      setIsUserFormOpen(true);
      // Clean up URL
      router.replace(window.location.pathname, { preserveState: true, preserveScroll: true });
    }
  }, [users, permissions]);

  // Reset all filters
  const resetFilters = () => {
    resetStoredFilters()
  }

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0
    if (filters.searchQuery) count++
    if (filters.roleFilter !== "All") count++
    if (filters.statusFilter !== "All") count++
    if (filters.departmentFilter !== "All") count++
    return count
  }

  // Handle column sorting
  const handleSort = (column) => {
    if (filters.sortColumn === column) {
      // Toggle direction if clicking the same column
      updateFilter('sortDirection', filters.sortDirection === "asc" ? "desc" : "asc")
    } else {
      // Set new column and default to ascending
      updateFilters({ sortColumn: column, sortDirection: "asc" })
    }
  }

  // Filter users based on search query and filters
  const filteredUsers = users.filter((user) => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
    const matchesSearch =
      fullName.includes(filters.searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
      String(user.id).toLowerCase().includes(filters.searchQuery.toLowerCase())

    const matchesRole = filters.roleFilter === "All" || user.role.toLowerCase() === filters.roleFilter.toLowerCase()
    const matchesStatus = filters.statusFilter === "All" || user.status === filters.statusFilter
    const matchesDepartment = filters.departmentFilter === "All" || user.department === filters.departmentFilter

    return matchesSearch && matchesRole && matchesStatus && matchesDepartment
  })

  // Sort the filtered users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let comparison = 0

    switch (filters.sortColumn) {
      case "id":
        // Handle numeric comparison for ID
        comparison = Number(a.id) - Number(b.id)
        break
      case "name":
        const aName = `${a.firstName} ${a.lastName}`
        const bName = `${b.firstName} ${b.lastName}`
        comparison = aName.localeCompare(bName)
        break
      case "email":
        comparison = a.email.localeCompare(b.email)
        break
      case "role":
        comparison = a.role.localeCompare(b.role)
        break
      case "status":
        comparison = a.status.localeCompare(b.status)
        break
      case "lastActive":
        comparison = a.lastActive.localeCompare(b.lastActive)
        break
      case "department":
        comparison = (a.department || "").localeCompare(b.department || "")
        break
      default:
        comparison = 0
    }

    return filters.sortDirection === "asc" ? comparison : -comparison
  })

  // Paginate the sorted users
  const paginatedUsers = sortedUsers.slice(filters.page * filters.rowsPerPage, filters.page * filters.rowsPerPage + filters.rowsPerPage)

  // Add this function to handle page changes
  const handlePageChange = (newPage) => {
    updateFilter('page', newPage)
  }

  // Update the handleUserSubmit function to use the user context
  const handleUserSubmit = async (userData) => {
    setIsSubmitting(true)

    try {
      let result
      if (selectedUser) {
        // Update existing user
        const updatedUser = {
          ...selectedUser,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          role: userData.role,
          status: userData.status,
          department_id: userData.department_id,
        }
        result = await updateUser(updatedUser)
      } else {
        // Create new user
        result = await addUser({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          role: userData.role,
          status: userData.status,
          department_id: userData.department_id,
        })
      }

      setIsSubmitting(false)

      if (result.success) {
        setIsUserFormOpen(false)
        // Show success toast
        toast({
          title: selectedUser ? "User Updated" : "User Created",
          description: `${userData.firstName} ${userData.lastName} has been ${selectedUser ? "updated" : "created"} successfully.`,
        })

        // Reset selected user
        setSelectedUser(null)
      } else {
        // Show error toast
        toast({
          title: "Error",
          description: result.error || "An error occurred while saving the user.",
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while saving the user.",
        variant: "destructive",
      })
      setIsSubmitting(false)
    }
  }

  // Add a function to view user details
  const handleViewUser = (user) => {
    // Format user data for the form
    const formattedUser = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      status: user.status,
      department_id: user.department_id,
    }

    setSelectedUser(formattedUser)
    setIsUserFormOpen(true)
  }

  const handleEditUser = (user) => {
    // Format user data for the form
    const formattedUser = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      status: user.status,
      department_id: user.department_id,
    }

    setSelectedUser(formattedUser)
    setIsUserFormOpen(true)
  }

  const handleDeleteUser = async (user) => {
    try {
      const result = await deleteUser(user.id)
      
      if (result.success) {
        toast({
          title: "User Deleted",
          description: `${user.firstName} ${user.lastName} has been deleted successfully.`,
        })
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to delete user.",
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while deleting the user.",
        variant: "destructive",
      })
    }
  }

  const handlePasswordReset = (user) => {
    setUserForPasswordReset(user)
    setIsPasswordResetModalOpen(true)
  }

  const handleManagePermissions = (user) => {
    setUserForPermissions(user)
    setIsPermissionsModalOpen(true)
  }

  const handleManageExternalIds = (user) => {
    setUserForExternalIds(user)
    setIsExternalUserModalOpen(true)
  }

  const handlePasswordResetConfirm = async () => {
    if (!userForPasswordReset) return
    
    setIsPasswordResetSubmitting(true)
    
    try {
      const response = await axios.post(`/users/${userForPasswordReset.id}/send-password-reset`)
      
      // Store user info for the toast before clearing state
      const userInfo = {
        firstName: userForPasswordReset.firstName,
        lastName: userForPasswordReset.lastName,
        email: userForPasswordReset.email
      }
      
      // Wait a moment to show the loading state, then close modal
      setTimeout(() => {
        setIsPasswordResetModalOpen(false)
        setUserForPasswordReset(null)
        setIsPasswordResetSubmitting(false)
        
        // Wait for modal to completely close, then show success toast
        setTimeout(() => {
          toast({
            title: "Password Reset Email Sent",
            description: `A password reset email has been sent to ${userInfo.firstName} ${userInfo.lastName} at ${userInfo.email}.`,
          })
        }, 400) // Wait for modal closing animation to complete
      }, 800)
      
    } catch (error) {
      console.error('Password reset error:', error)
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to send password reset email.",
        variant: "destructive",
      })
      setIsPasswordResetSubmitting(false)
    }
  }

  // Helper function to get role color
  const getRoleColor = (role) => {
    switch (role.toLowerCase()) {
      case "admin":
        return "bg-red-100 text-red-800"
      case "manager":
        return "bg-blue-100 text-blue-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800"
      case "Inactive":
        return "bg-gray-100 text-gray-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  // Helper function to render sort indicator
  const renderSortIndicator = (column) => {
    if (filters.sortColumn !== column) return null
    return filters.sortDirection === "asc" ? (
      <ArrowUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4 inline" />
    )
  }

  // Handle impersonate user
  const handleImpersonateUser = (user) => {
    router.post(`/impersonate/${user.id}`, {}, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: `Now viewing as ${user.firstName} ${user.lastName}`,
        })
        // Reload the page to refresh the view
        window.location.reload()
      },
      onError: (errors) => {
        toast({
          title: "Error",
          description: errors.message || "Failed to impersonate user.",
          variant: "destructive",
        })
      }
    })
  }

  return (
    <div className="flex flex-col gap-6 pb-8 pt-6 px-4 md:px-6 w-full">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">Users</h1>
        <p className="text-muted-foreground">Manage users and their permissions</p>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search users..."
              className="pl-10 w-[200px] md:w-[300px]"
              value={filters.searchQuery}
              onChange={(e) => updateFilter('searchQuery', e.target.value)}
            />
          </div>

          <Select value={filters.roleFilter} onValueChange={(value) => updateFilter('roleFilter', value)}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="user">User</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.statusFilter} onValueChange={(value) => updateFilter('statusFilter', value)}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.departmentFilter} onValueChange={(value) => updateFilter('departmentFilter', value)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Departments</SelectItem>
              <SelectItem value="Unassigned">Unassigned</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.name}>
                  {dept.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {getActiveFilterCount() > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <X className="mr-2 h-4 w-4" />
              Reset
            </Button>
          )}
        </div>
      </div>

      <div className="text-sm text-muted-foreground">
        {sortedUsers.length} user{sortedUsers.length !== 1 ? "s" : ""} found
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px] cursor-pointer" onClick={() => handleSort("id")}>
                    ID {renderSortIndicator("id")}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("name")}>
                    User {renderSortIndicator("name")}
                  </TableHead>
                  <TableHead className="hidden md:table-cell cursor-pointer" onClick={() => handleSort("email")}>
                    Email {renderSortIndicator("email")}
                  </TableHead>
                  <TableHead className="hidden md:table-cell cursor-pointer" onClick={() => handleSort("role")}>
                    Role {renderSortIndicator("role")}
                  </TableHead>
                  <TableHead className="hidden lg:table-cell cursor-pointer" onClick={() => handleSort("department")}>
                    Department {renderSortIndicator("department")}
                  </TableHead>
                  <TableHead className="hidden lg:table-cell cursor-pointer" onClick={() => handleSort("status")}>
                    Status {renderSortIndicator("status")}
                  </TableHead>
                  <TableHead className="hidden xl:table-cell cursor-pointer" onClick={() => handleSort("lastActive")}>
                    Last Active {renderSortIndicator("lastActive")}
                  </TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewUser(user)}
                    >
                      <TableCell>{user.id}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar} alt={user.name} />
                            <AvatarFallback>
                              {user.firstName[0]}
                              {user.lastName[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{`${user.firstName} ${user.lastName}`}</div>
                            <div className="text-sm text-muted-foreground md:hidden">{user.email}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{user.email}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="outline" className={getRoleColor(user.role)}>
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="text-sm">
                          {user.department || "Unassigned"}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <Badge variant="outline" className={getStatusColor(user.status)}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden xl:table-cell">{user.lastActive}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Open menu</span>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            {permissions?.can_impersonate_users && user.id !== currentUser?.id && (
                              <DropdownMenuItem onClick={() => handleImpersonateUser(user)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View as User
                              </DropdownMenuItem>
                            )}
                            {permissions?.can_edit_users && (
                              <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit User
                              </DropdownMenuItem>
                            )}
                            {permissions?.can_edit_users && (
                              <DropdownMenuItem onClick={() => handlePasswordReset(user)}>
                                <Key className="mr-2 h-4 w-4" />
                                Reset Password
                              </DropdownMenuItem>
                            )}
                            {permissions?.can_edit_users && (
                              <DropdownMenuItem onClick={() => handleManageExternalIds(user)}>
                                <Database className="mr-2 h-4 w-4" />
                                External IDs
                              </DropdownMenuItem>
                            )}
                            {permissions?.can_edit_department_user_permissions && (
                              <DropdownMenuItem onClick={() => handleManagePermissions(user)}>
                                <Shield className="mr-2 h-4 w-4" />
                                Manage Permissions
                              </DropdownMenuItem>
                            )}
                            {(permissions?.can_edit_users || permissions?.can_delete_users || permissions?.can_edit_department_user_permissions || (permissions?.can_impersonate_users && user.id !== currentUser?.id)) && (
                              <DropdownMenuSeparator />
                            )}
                            {permissions?.can_delete_users && (
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleDeleteUser(user)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {paginatedUsers.length} of {users.length} users
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={() => handlePageChange(filters.page - 1)} disabled={filters.page === 0}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {Array.from({ length: Math.ceil(sortedUsers.length / filters.rowsPerPage) })
            .map((_, index) => (
              <Button
                key={index}
                variant={filters.page === index ? "default" : "outline"}
                size="sm"
                className="h-8 w-8 dark:text-white"
                onClick={() => handlePageChange(index)}
              >
                {index + 1}
              </Button>
            ))
            .slice(Math.max(0, filters.page - 1), Math.min(filters.page + 2, Math.ceil(sortedUsers.length / filters.rowsPerPage)))}
          <Button
            variant="outline"
            size="icon"
            onClick={() => handlePageChange(filters.page + 1)}
            disabled={filters.page >= Math.ceil(sortedUsers.length / filters.rowsPerPage) - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <UserFormModal
        open={isUserFormOpen}
        onOpenChange={setIsUserFormOpen}
        initialData={selectedUser}
        onSubmit={handleUserSubmit}
        isSubmitting={isSubmitting}
        departments={departments}
      />

      <PasswordResetConfirmModal
        open={isPasswordResetModalOpen}
        onOpenChange={setIsPasswordResetModalOpen}
        user={userForPasswordReset}
        onConfirm={handlePasswordResetConfirm}
        isSubmitting={isPasswordResetSubmitting}
      />

      <UserPermissionsModal
        open={isPermissionsModalOpen}
        onOpenChange={setIsPermissionsModalOpen}
        user={userForPermissions}
        onSuccess={() => {
          // Optionally refresh user data or show success message
          toast({
            title: "Success",
            description: "User permissions updated successfully.",
          })
        }}
      />

      <ExternalUserModal
        open={isExternalUserModalOpen}
        onOpenChange={setIsExternalUserModalOpen}
        user={userForExternalIds}
        integrations={integrations}
        canEdit={permissions?.can_edit_users}
      />
    </div>
  )
}
